import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, X } from "lucide-react";
import { shopApi } from "@/lib/api";
import FreeShippingBar from "@/components/FreeShippingBar";

/**
 * Live cart totals footer — hits /api/checkout/quote so shoppers see
 * bundle + coupon + tier discounts inside the drawer, without going to checkout.
 */
export default function CartFooter({ items, subtotal, onCheckout }) {
  const [coupon, setCoupon] = React.useState("");
  const [couponInputOpen, setCouponInputOpen] = React.useState(false);
  const [quote, setQuote] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [couponError, setCouponError] = React.useState("");

  // Pull auto-applied coupon from Exit-Intent (or previously entered) on first mount
  React.useEffect(() => {
    try {
      const pending = localStorage.getItem("novalynx_pending_coupon");
      if (pending) { setCoupon(pending); setCouponInputOpen(true); }
    } catch { /* ignore */ }
  }, []);

  // Re-quote whenever items or coupon change (debounced)
  React.useEffect(() => {
    if (items.length === 0) { setQuote(null); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const q = await shopApi.quote({
          items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, bundle_id: i.bundle_id })),
          shipping_option: "standard",
          coupon_code: coupon || undefined,
          origin_url: window.location.origin,
        });
        setQuote(q);
        setCouponError(q.coupon_code || !coupon ? "" : "Coupon not applied");
      } catch (e) {
        setQuote(null);
        if (coupon) setCouponError(e.response?.data?.detail || "Coupon invalid");
      }
      setLoading(false);
    }, 350);
    return () => clearTimeout(t);
  }, [items, coupon]);

  const clearCoupon = () => {
    setCoupon(""); setCouponError("");
    try { localStorage.removeItem("novalynx_pending_coupon"); } catch { /* ignore */ }
  };

  // What to show as the discounted subtotal (after all product-level discounts)
  const discountedItemsTotal = quote
    ? Math.max(quote.subtotal - (quote.bundle_discount || 0) - (quote.coupon_discount || 0) - (quote.tier_discount || 0), 0)
    : subtotal;

  return (
    <div className="border-t border-white/10 pt-4 space-y-3" data-testid="cart-footer">
      <FreeShippingBar subtotal={discountedItemsTotal} threshold={quote?.free_shipping_threshold} compact />

      {/* Coupon row */}
      {couponInputOpen ? (
        <div className="space-y-1" data-testid="cart-coupon-row">
          <div className="flex gap-2">
            <Input
              placeholder="Coupon code"
              value={coupon}
              onChange={(e) => { setCoupon(e.target.value.toUpperCase()); setCouponError(""); }}
              className="h-9 text-sm"
              data-testid="cart-coupon-input"
            />
            {coupon && (
              <button onClick={clearCoupon} className="text-xs text-muted-foreground hover:text-white shrink-0 px-2" data-testid="cart-coupon-clear">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {couponError && <p className="text-xs text-destructive" data-testid="cart-coupon-error">{couponError}</p>}
          {quote?.coupon_code && !couponError && (
            <p className="text-xs text-primary" data-testid="cart-coupon-applied">✓ {quote.coupon_code} applied</p>
          )}
        </div>
      ) : (
        <button onClick={() => setCouponInputOpen(true)} className="text-xs text-muted-foreground hover:text-primary transition" data-testid="cart-coupon-open">
          Have a coupon code?
        </button>
      )}

      {/* Totals breakdown */}
      <div className="space-y-1.5 text-sm" data-testid="cart-totals">
        <Row label="Subtotal" value={quote?.subtotal ?? subtotal} loading={loading && !quote} />
        {quote?.bundle_discount > 0 && <Row label="Bundle discount" value={-quote.bundle_discount} accent />}
        {quote?.coupon_discount > 0 && <Row label={`Coupon (${quote.coupon_code})`} value={-quote.coupon_discount} accent />}
        {quote?.tier_discount > 0 && <Row label={`${quote.tier_name || "Loyalty"} tier`} value={-quote.tier_discount} accent />}
        {quote && (
          <Row
            label="Shipping"
            value={quote.shipping}
            free={quote.free_shipping_unlocked || quote.shipping === 0}
          />
        )}
        {quote?.tax > 0 && <Row label="Tax (est.)" value={quote.tax} muted />}
        <div className="border-t border-white/5 pt-2 mt-2 flex justify-between items-baseline">
          <span className="font-semibold">Total</span>
          <span className="font-display text-xl font-bold text-primary" data-testid="cart-total">
            {loading && !quote ? <Loader2 className="h-4 w-4 animate-spin" /> : `$${(quote?.total ?? subtotal).toFixed(2)}`}
          </span>
        </div>
      </div>

      <Button className="btn-primary w-full" onClick={onCheckout} data-testid="cart-checkout-btn">
        Checkout · ${(quote?.total ?? subtotal).toFixed(2)}
      </Button>
    </div>
  );
}

function Row({ label, value, accent, muted, free, loading }) {
  const display = loading
    ? <Loader2 className="h-3 w-3 animate-spin" />
    : free
      ? <span className="text-primary font-semibold">FREE</span>
      : `${value < 0 ? "−" : ""}$${Math.abs(value).toFixed(2)}`;
  return (
    <div className={`flex justify-between ${accent ? "text-primary" : muted ? "text-muted-foreground" : ""}`}>
      <span>{label}</span>
      <span>{display}</span>
    </div>
  );
}
